import type { ReduxAction } from '@finn-no/redux-actions';
import { IconClose16, IconPlus16 } from '@warp-ds/icons/react';
import { Button } from '@warp-ds/react';
import cn from 'classnames';

import { t } from '../../locale/texts';
import { CustomNumberInput } from '../common/CustomNumberInput';
import { CustomSelect } from '../common/CustomSelect';
import { childAgeOptions } from '../common/utils/filters';
import type { HotelRoom } from '../types';
import actions from './hotelActions';

type HotelRoomsProps = {
    rooms: HotelRoom[];
    dispatch: (action: ReduxAction) => void;
};

export const HotelRooms = ({ rooms, dispatch }: HotelRoomsProps) => {
    const updateRoom = (roomIndex: number, updatedRoom: HotelRoom) => {
        dispatch(actions.setRooms({ index: roomIndex, ...updatedRoom }));
    };

    const setAdults = (roomIndex: number, newAdults: number) => {
        updateRoom(roomIndex, {
            adults: newAdults,
            children: rooms[roomIndex].children,
        });
    };

    const setChildrenCount = (roomIndex: number, newCount: number) => {
        const currentChildren = rooms[roomIndex].children;
        const adjustedChildren =
            newCount > currentChildren.length
                ? [...currentChildren, ...new Array(newCount - currentChildren.length).fill(0)]
                : currentChildren.slice(0, newCount);

        updateRoom(roomIndex, {
            adults: rooms[roomIndex].adults,
            children: adjustedChildren,
        });
    };

    const setChildAge = (roomIndex: number, childIndex: number, age: number) => {
        const children = [...rooms[roomIndex].children];
        children[childIndex] = age;

        updateRoom(roomIndex, {
            adults: rooms[roomIndex].adults,
            children,
        });
    };

    return (
        <>
            {rooms.map((room, roomIndex) => (
                <fieldset key={`hotelRoom-${roomIndex}-${room.adults}-${room.children.length}`} className="flex flex-col gap-12">
                    <div className={cn('flex flex-row items-center', roomIndex > 0 && 'mt-16')}>
                        <legend data-testid={`hotel-room-${roomIndex}`} className="font-bold">
                            {t('hotel.room.main', { count: roomIndex + 1 })}
                        </legend>
                        {roomIndex > 0 && (
                            <Button
                                secondary
                                quiet
                                type="button"
                                data-testid="remove-room"
                                onClick={() => dispatch(actions.removeRoom(rooms))}
                                className="flex flex-row items-center justify-center gap-8 text-xs w-full">
                                <IconClose16 aria-hidden="true" focusable="false" />
                                Slett rom
                            </Button>
                        )}
                    </div>
                    <CustomNumberInput
                        id={`adults-${roomIndex}`}
                        label={t('flight.passengers.inputs.adultLabel')}
                        value={room.adults}
                        setNumber={(val) => setAdults(roomIndex, val)}
                        min={1}
                        max={4}
                        disableSubtract={room.adults <= 1}
                        disableAdd={room.adults >= 4}
                    />
                    <CustomNumberInput
                        id={`children-${roomIndex}`}
                        label={t('flight.passengers.inputs.childLabel')}
                        helpText="0-17 år"
                        value={room.children.length}
                        setNumber={(val) => setChildrenCount(roomIndex, val)}
                        min={0}
                        max={4}
                        disableSubtract={room.children.length <= 0}
                        disableAdd={room.children.length >= 4}
                    />
                    {room.children.map((childAge, childIndex) => (
                        <CustomSelect
                            key={`childAge-${roomIndex}-${childIndex}-${childAge}`}
                            value={childAge.toString()}
                            onChange={(val) => setChildAge(roomIndex, childIndex, Number.parseInt(val))}
                            options={childAgeOptions}
                            triggerLabel={t('hotel.room.childAge', { number: childIndex + 1 })}
                            hideLabelOnSmallScreen={false}
                            fullscreenTitle={t('hotel.room.childAge', { number: childIndex + 1 })}
                            relative
                        />
                    ))}
                </fieldset>
            ))}
            <Button
                className="flex flex-row items-center justify-center gap-8 mt-16"
                fullWidth
                secondary
                type="button"
                data-testid="add-room"
                onClick={() => dispatch(actions.addRoom(rooms))}>
                <IconPlus16 aria-hidden /> {t('hotel.room.add')}
            </Button>
        </>
    );
};
